import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, extends as _extends } from './_virtual/_rollupPluginBabelHelpers.js';
import { useTrueAtMediaBreakpointLessThen, useResponsiveValue, Flex, Skeleton, Button } from '@costar/theme-ui';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

var _excluded = ["selected"],
  _excluded2 = ["navType"],
  _excluded3 = ["currentPageNumber", "pageSize", "total", "onPageChange", "isSmallContainer", "isPaginatorDisabled", "isTruncatedWidth", "isLoading", "gap"];
var getTotalPageCount = function getTotalPageCount(nextProps) {
  var _ref = nextProps !== null && nextProps !== void 0 ? nextProps : {},
    _ref$total = _ref.total,
    total = _ref$total === void 0 ? 0 : _ref$total,
    _ref$pageSize = _ref.pageSize,
    pageSize = _ref$pageSize === void 0 ? 1 : _ref$pageSize;
  return Math.ceil(total / pageSize) || 1;
};
var getCurrentPageNumber = function getCurrentPageNumber(nextProps) {
  var _ref2 = nextProps !== null && nextProps !== void 0 ? nextProps : {},
    _ref2$currentPageNumb = _ref2.currentPageNumber,
    currentPageNumber = _ref2$currentPageNumb === void 0 ? 1 : _ref2$currentPageNumb;
  return currentPageNumber > 0 && currentPageNumber <= getTotalPageCount(nextProps) ? currentPageNumber : 1;
};
var getEllipsesPage = function getEllipsesPage() {
  var eSide = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'right';
  return {
    label: '...',
    itemType: 'ellipses',
    ellipsesSide: eSide
  };
};
var getPage = function getPage(id, label) {
  return {
    id: id,
    label: label,
    itemType: 'page'
  };
};
var getPageRange = function getPageRange(start, end) {
  var pages = [];
  for (var i = start; i <= end; i++) {
    pages.push(getPage(i, i.toString()));
  }
  return pages;
};
var PaginationItem = function PaginationItem(props) {
  var selected = props.selected,
    rest = _objectWithoutProperties(props, _excluded);
  return /*#__PURE__*/React.createElement(Button, _extends({
    isStatic: selected,
    variant: "ghost",
    color: selected ? 'black' : undefined
  }, rest));
};
var Navigation = function Navigation(props) {
  var navType = props.navType,
    rest = _objectWithoutProperties(props, _excluded2);
  var iconName = navType === 'prev' ? 'chevronLeft' : 'chevronRight';
  return /*#__PURE__*/React.createElement(PaginationItem, _extends({
    icon: iconName,
    iconSize: "xl"
  }, rest));
};
var skeletonProps = {
  variant: 'rect',
  width: '2em',
  height: '2.375em' // provide the same height space for the PaginationItem minimize layout shift
};

var responsiveCondensePlaceHolderArray = [[0, 1, 2, 3], [0, 1, 2, 3], [0, 1, 2, 3], [0, 1, 2, 3], [0, 1, 2, 3], [0, 1, 2, 3, 4, 5]];
var responsivePlaceHolderArray = [[0, 1, 2, 3], [0, 1, 2, 3], [0, 1, 2, 3], [0, 1, 2, 3, 4, 5], [0, 1, 2, 3, 4, 5, 6, 7]];
var Paginator = function Paginator(props) {
  var currentPageNumber = props.currentPageNumber,
    pageSize = props.pageSize,
    total = props.total,
    onPageChange = props.onPageChange,
    isSmallContainer = props.isSmallContainer,
    isPaginatorDisabled = props.isPaginatorDisabled,
    isTruncatedWidth = props.isTruncatedWidth,
    isLoading = props.isLoading,
    _props$gap = props.gap,
    gap = _props$gap === void 0 ? 'xxl' : _props$gap,
    rest = _objectWithoutProperties(props, _excluded3);
  var isMobile = useTrueAtMediaBreakpointLessThen(isTruncatedWidth ? 'sm' : 'xs'); // less then 900px when filter is visible other wise less then 600px
  var placeholderArray = useResponsiveValue(isTruncatedWidth ? responsiveCondensePlaceHolderArray : responsivePlaceHolderArray);
  var pageNumber = getCurrentPageNumber({
    currentPageNumber: currentPageNumber,
    pageSize: pageSize,
    total: total
  });
  var totalPageCount = getTotalPageCount({
    pageSize: pageSize,
    total: total
  });
  var _useState = useState(pageNumber < totalPageCount),
    _useState2 = _slicedToArray(_useState, 2),
    enableNext = _useState2[0],
    setEnableNext = _useState2[1];
  var _useState3 = useState(pageNumber > 1),
    _useState4 = _slicedToArray(_useState3, 2),
    enablePrevious = _useState4[0],
    setEnablePrevious = _useState4[1];
  useEffect(function () {
    setEnableNext(!isPaginatorDisabled && pageNumber < totalPageCount);
    setEnablePrevious(!isPaginatorDisabled && pageNumber > 1);
  }, [isPaginatorDisabled, pageNumber, totalPageCount]);
  var triggerPageChange = useCallback(function (pageNumber) {
    if (onPageChange) {
      onPageChange(pageNumber);
    }
  }, [onPageChange]);
  var onPageClick = useCallback(function (page) {
    return function () {
      var _pageNumber = pageNumber;
      var id = page.id,
        itemType = page.itemType,
        ellipsesSide = page.ellipsesSide;
      if (itemType === 'ellipses') {
        if (ellipsesSide === 'left') {
          _pageNumber = _pageNumber - 2;
        } else {
          _pageNumber = _pageNumber + 2;
        }
        triggerPageChange(pageNumber);
      } else if (_pageNumber !== id) {
        setEnablePrevious(id > 1);
        setEnableNext(id < totalPageCount);
        triggerPageChange(id);
      }
    };
  }, [pageNumber, totalPageCount, triggerPageChange]);
  var next = useCallback(function () {
    if (!enableNext) {
      return;
    }
    setEnablePrevious(pageNumber + 2 > 1);
    setEnableNext(pageNumber + 1 < totalPageCount);
    if (pageNumber + 1 <= totalPageCount) {
      triggerPageChange(pageNumber + 1);
    }
  }, [enableNext, pageNumber, totalPageCount, triggerPageChange]);
  var previous = useCallback(function () {
    if (!enablePrevious) {
      return;
    }
    setEnablePrevious(pageNumber - 1 > 1);
    setEnableNext(pageNumber - 1 < totalPageCount);
    if (pageNumber > 1) {
      triggerPageChange(pageNumber - 1);
    }
  }, [enablePrevious, pageNumber, totalPageCount, triggerPageChange]);
  var calculatedPages = useMemo(function () {
    //Place ellipses as per AC
    if (totalPageCount >= placeholderArray.length) {
      var pages = [];
      if (pageNumber <= placeholderArray.length - 3) {
        pages.push.apply(pages, _toConsumableArray(getPageRange(1, placeholderArray.length - 1)));
        pages.push(getEllipsesPage());
        pages.push(getPage(totalPageCount, totalPageCount.toString()));
      } else if (pageNumber > placeholderArray.length - 3) {
        pages.push(getPage(1, '1'));
        pages.push(getEllipsesPage('left'));
        var second = pageNumber - 2;
        var first = pageNumber - 1;
        pages.push(getPage(second, second.toString()));
        pages.push(getPage(first, first.toString()));
        pages.push(getPage(pageNumber, pageNumber.toString()));
        if (pageNumber + 3 <= totalPageCount) {
          var third = pageNumber + 1;
          var forth = pageNumber + 2;
          pages.push(getPage(third, third.toString()));
          pages.push(getPage(forth, forth.toString()));
          if (pageNumber + 3 === totalPageCount) {
            pages.push(getPage(pageNumber + 3, (pageNumber + 3).toString()));
          } else {
            pages.push(getEllipsesPage());
            pages.push(getPage(totalPageCount, totalPageCount.toString()));
          }
        } else if (pageNumber !== totalPageCount) {
          var counter = pageNumber + 1;
          while (counter < totalPageCount) {
            pages.push(getPage(counter, counter.toString()));
            counter++;
          }
          pages.push(getPage(totalPageCount, totalPageCount.toString()));
        }
      }
      return pages;
    }

    //If total pages are labeled as it is.
    return _toConsumableArray(getPageRange(1, totalPageCount));
  }, [pageNumber, totalPageCount, placeholderArray]);
  return /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Flex, _extends({
    gap: gap,
    alignItems: "center"
  }, rest), isLoading ? /*#__PURE__*/React.createElement(Skeleton, skeletonProps) : /*#__PURE__*/React.createElement(Navigation, {
    navType: "prev",
    disabled: !enablePrevious,
    onClick: previous
  }), isMobile || isSmallContainer ? isLoading ? /*#__PURE__*/React.createElement(Skeleton, skeletonProps) : /*#__PURE__*/React.createElement(PaginationItem, {
    selected: true
  }, pageNumber) : isLoading ? placeholderArray.map(function (_i, indx) {
    return /*#__PURE__*/React.createElement(Skeleton, {
      key: "pagination-item-".concat(indx),
      variant: "rect",
      width: "2em",
      height: "2.375em"
    });
  }) : calculatedPages.map(function (page, indx) {
    return /*#__PURE__*/React.createElement(PaginationItem, {
      key: "pagination-item-".concat(indx),
      onClick: onPageClick(page),
      selected: pageNumber === parseInt(page.label)
    }, page.label);
  }), isLoading ? /*#__PURE__*/React.createElement(Skeleton, skeletonProps) : /*#__PURE__*/React.createElement(Navigation, {
    navType: "next",
    disabled: !enableNext,
    onClick: next
  })));
};

export default Paginator;
//# sourceMappingURL=paginator.esm.js.map
